import { useCallback, useState } from "react";
// Import Orval-generated API clients
import { 
    useSearchCustomers as useSearchCustomersClient, 
    useGetCustomer as useGetCustomerClient, 
    useGetProducts as useGetProductsClient,
    useGetProductByType as useGetProductByTypeClient,
    useGetAllProducts2
} from '@/shared/api/client/customers';
import { UseQueryResult } from '@tanstack/react-query';
import { CustomerDTO, ProductDTO,AuthUser } from '@/shared/api/model';
// import { normalizeProductDocumentConfig } from '@/modules/loans/utils/productAdapter';

// Stub for missing normalizeProductDocumentConfig
const normalizeProductDocumentConfig = (item: any) => item;

export function useCustomerSearch(initialBranch: string): {
    isLoading: boolean;
    isError: boolean;
    data: CustomerDTO[] | undefined;
    searchCustomers: (branch: string, query: string) => void;
    refetch: () => void;
    error: unknown;
} {
    const [searchParams, setSearchParams] = useState({
        branch: initialBranch,
        query: ''
    });

    const result = useSearchCustomersClient(searchParams.branch, 
        { query: searchParams.query },
        {
            query: {
                queryKey: ['customers', 'search', searchParams.branch, searchParams.query],
                enabled: !!searchParams.query,
                staleTime: 5 * 60 * 1000 // 5 minutes
            }
        }
    );

    const searchCustomers = useCallback((branch: string, query: string) => {
        setSearchParams({branch, query});
    }, []);

    return {
        isLoading: result.isPending,
        isError: result.isError,
        data: result.data,
        searchCustomers,
        refetch: result.refetch,
        error: result.error
    };
}

export function useGetCustomer(branch: string, custNo: string): {
    isLoading: boolean;
    isError: boolean;
    data: CustomerDTO | undefined;
} {
    const result = useGetCustomerClient(branch, custNo, {
        query: {
            queryKey: ['customer', branch, custNo],
            enabled: !!custNo
        }
    });

    return {
        isLoading: result.isPending,
        isError: result.isError,
        data: result.data
    };
}

export function useGetProducts({branch, custNo}: AuthUser, product: string): {
    isLoading: boolean;
    isError: boolean;
    data: ProductDTO[] | undefined;
    error: unknown;
}[] {
    // @ts-ignore
    const result = useGetProductsClient(branch, custNo, { product }, {
        query: {
            queryKey: ['products', branch, custNo, product]
        }
    });
    
    // Return array pattern to maintain backward compatibility with components
    return [
        {
            isLoading: result.isPending,
            isError: result.isError,
            data: result.data?.map((item) => normalizeProductDocumentConfig(item) as ProductDTO),
            error: result.error
        }
    ];
}

export function useGetProductByType(product: string): {
    isLoading: boolean;
    isError: boolean;
    data: ProductDTO[] | undefined;
    error: unknown;
}[] {
    const result = useGetProductByTypeClient(product, {
        query: {
            queryKey: ['products', product]
        }
    });

    // Return array pattern to maintain backward compatibility with components
    return [
        {
            isLoading: result.isPending,
            isError: result.isError,
            data: result.data?.map((item) => normalizeProductDocumentConfig(item) as ProductDTO),
            error: result.error
        }
    ];
}

export function useGetAllProducts(): {
    isLoading: boolean;
    isError: boolean;
    data: ProductDTO[] | undefined;
    error: unknown;
}[] {
    const result = useGetAllProducts2({
        query: {
            queryKey: ['allProducts']
        }
    });

    // Return array pattern to maintain backward compatibility with components
    return [
        {
            isLoading: result.isPending,
            isError: result.isError,
            data: result.data?.map((item) => normalizeProductDocumentConfig(item) as ProductDTO),
            error: result.error
        }
    ];
}

// Add backward compatibility exports for components still using the old names
export { useCustomerSearch as useSearchCustomers }